"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Activity, Lock, Terminal, CircleAlert, CircleCheck, FileCode } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const AUDIT_LOGS = [{
  id: "SIG-001",
  time: "09:42:11 UTC",
  action: "VENTURE_VETTING",
  target: "PROJECT_ALPHA",
  status: "PROCESSING"
}, {
  id: "SIG-002",
  time: "09:42:15 UTC",
  action: "MARKET_VALIDATION",
  target: "CORE_MODEL_V4",
  status: "VERIFIED"
}, {
  id: "SIG-003",
  time: "09:42:48 UTC",
  action: "MODEL_STRESS_TEST",
  target: "DATA_SET_X",
  status: "SUCCESS"
}, {
  id: "SIG-004",
  time: "09:43:02 UTC",
  action: "CAPITAL_DEPLOY",
  target: "SERIES_A_V3",
  status: "COMPLETED"
}, {
  id: "SIG-005",
  time: "09:43:15 UTC",
  action: "PIVOT_DETECTED",
  target: "NODE_77_EXT",
  status: "FLAGGED"
}, {
  id: "SIG-006",
  time: "09:43:22 UTC",
  action: "OPS_OPTIMIZATION",
  target: "NODE_77_EXT",
  status: "ISOLATED"
}, {
  id: "SIG-007",
  time: "09:43:45 UTC",
  action: "FOUNDER_SYNC",
  target: "ADMIN_01",
  status: "GRANTED"
}, {
  id: "SIG-008",
  time: "09:44:10 UTC",
  action: "GROWTH_AUDIT",
  target: "MAIN_REVENUE",
  status: "OPTIMAL"
}, {
  id: "SIG-009",
  time: "09:44:33 UTC",
  action: "IP_PROTECTION",
  target: "SECURE_VAULT",
  status: "UPDATED"
}, {
  id: "SIG-010",
  time: "09:45:01 UTC",
  action: "PMF_ANALYSIS",
  target: "ASSET_BETA_2",
  status: "PROCESSING"
}, {
  id: "SIG-011",
  time: "09:45:18 UTC",
  action: "TRACTION_CHECK",
  target: "ASSET_BETA_2",
  status: "ANALYZING"
}, {
  id: "SIG-012",
  time: "09:45:42 UTC",
  action: "DUE_DILIGENCE",
  target: "EXT_REQUEST",
  status: "REJECTED"
}, {
  id: "SIG-013",
  time: "09:46:05 UTC",
  action: "DATA_ROOM_SYNC",
  target: "DB_SHARD_04",
  status: "SYNCED"
}, {
  id: "SIG-014",
  time: "09:46:29 UTC",
  action: "SCALE_VALIDATION",
  target: "MODEL_LLM_7B",
  status: "VERIFIED"
}, {
  id: "SIG-015",
  time: "09:47:12 UTC",
  action: "EXIT_STRATEGY",
  target: "SESSION_ID_99",
  status: "COMPLETED"
}];
export default function TheVensigMandate() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  useEffect(() => {
    const scrollContainer = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!scrollContainer || !autoScroll) return;
    let animationFrameId: number;
    let lastTime = 0;
    const speed = 0.5;
    const animate = (time: number) => {
      if (!lastTime) lastTime = time;
      const deltaTime = time - lastTime;
      if (deltaTime > 16) {
        if (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1) {
          scrollContainer.scrollTop = 0;
        } else {
          scrollContainer.scrollTop += speed;
        }
        lastTime = time;
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [autoScroll]);
  return <section data-section-id="58518" id="our-positioning" className="dark bg-background text-foreground w-full border-y border-border overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[800px] lg:h-[800px]">
        
        {}
        <div className="w-full lg:w-[60%] p-8 md:p-16 lg:p-24 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-border relative bg-background">
          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(182,141,102,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(182,141,102,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />
          
          <div className="relative z-10 space-y-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-primary/20 bg-primary/5 text-primary text-xs font-mono uppercase tracking-widest w-fit">
              <ShieldCheck className="w-3 h-3" />
              <span>1066 Ventures Positioning</span>
            </div>

            <h2 className="text-5xl md:text-6xl lg:text-7xl font-default font-medium tracking-tighter leading-[0.9] text-foreground">
              BEYOND THE <br />
              <span className="text-muted-foreground">AGENCY MODEL</span>
            </h2>

            <div className="space-y-6 max-w-2xl">
              <p className="text-xl md:text-2xl font-default font-normal leading-relaxed text-muted-foreground border-l-2 border-primary pl-6">
                To establish absolute clarity in venture growth through rigorous operator involvement, tactical execution, and strategic standardization.
              </p>
              
              <p className="text-base text-muted-foreground/80 leading-relaxed font-default">
                In an era of superficial growth, 1066 stands as the definitive partner for founders. We do not merely advise; we build, validate, and scale the operational architecture of high-potential ventures.
              </p>
            </div>

            <div className="pt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-none h-14 px-8 text-base font-mono uppercase tracking-wider border border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                <Link to="#">
                  <FileCode className="mr-2 w-4 h-4" />
                  Our Services
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="rounded-none h-14 px-8 text-base font-mono uppercase tracking-wider border-border hover:bg-accent hover:text-accent-foreground transition-colors">
                <Link to="#">
                  <Lock className="mr-2 w-4 h-4" />
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-[40%] bg-card/50 flex flex-col relative">
          {}
          <div className="p-6 border-b border-border flex items-center justify-between bg-card">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-primary animate-pulse" />
              <h3 className="font-mono text-sm uppercase tracking-widest text-foreground">
                Venture Intelligence Feed
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              <span className="text-xs font-mono text-muted-foreground">ACTIVE</span>
            </div>
          </div>

          {}
          <div className="flex-1 relative overflow-hidden" onMouseEnter={() => setAutoScroll(false)} onMouseLeave={() => setAutoScroll(true)}>
            <ScrollArea ref={scrollRef} className="h-full w-full">
              <div className="flex flex-col divide-y divide-border/40">
                {AUDIT_LOGS.map((log, i) => <div key={log.id} data-index={i} className="p-4 hover:bg-white/5 transition-colors group cursor-default">
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        [{log.time}]
                      </span>
                      <StatusBadge status={log.status} />
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <Terminal className="w-3 h-3 text-muted-foreground/50" />
                      <span className="font-mono text-sm font-medium text-foreground tracking-tight">
                        {log.action}
                      </span>
                    </div>
                    <div className="pl-5 font-mono text-xs text-muted-foreground/70 truncate">
                      TARGET: <span className="text-foreground/80">{log.target}</span> 
                    </div>
                  </div>)}
                {}
                {AUDIT_LOGS.map((log, i) => <div key={`${log.id}-dup`} data-index={`dup-${i}`} className="p-4 hover:bg-white/5 transition-colors group cursor-default opacity-50" aria-hidden="true">
                     <div className="flex items-start justify-between mb-1">
                      <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        [{log.time}]
                      </span>
                      <StatusBadge status={log.status} />
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <Terminal className="w-3 h-3 text-muted-foreground/50" />
                      <span className="font-mono text-sm font-medium text-foreground tracking-tight">
                        {log.action}
                      </span>
                    </div>
                    <div className="pl-5 font-mono text-xs text-muted-foreground/70 truncate">
                      TARGET: <span className="text-foreground/80">{log.target}</span> 
                    </div>
                  </div>)}
              </div>
            </ScrollArea>
            
            {}
            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-card to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-card to-transparent pointer-events-none" />
          </div>

          {}
          <div className="p-4 border-t border-border bg-card text-xs font-mono text-muted-foreground flex justify-between items-center">
            <span>STATUS: OPERATIONAL</span>
            <span>1066_CORE_V1.0</span>
          </div>
        </div>
      </div>
    </section>;
}
function StatusBadge({
  status
}: {
  status: string;
}) {
  let colorClass = "text-muted-foreground";
  let icon = <Activity className="w-3 h-3" />;
  switch (status) {
    case "VERIFIED":
    case "SUCCESS":
    case "COMPLETED":
    case "GRANTED":
    case "OPTIMAL":
    case "SYNCED":
    case "UPDATED":
      colorClass = "text-chart-1";
      icon = <CircleCheck className="w-3 h-3" />;
      break;
    case "PROCESSING":
    case "ANALYZING":
      colorClass = "text-chart-4";
      icon = <Activity className="w-3 h-3 animate-spin" style={{
        animationDuration: '3s'
      }} />;
      break;
    case "FLAGGED":
    case "ISOLATED":
    case "REJECTED":
      colorClass = "text-destructive";
      icon = <CircleAlert className="w-3 h-3" />;
      break;
  }
  return <div className={`flex items-center gap-1.5 ${colorClass} font-mono text-[10px] uppercase tracking-wider border border-current/20 px-1.5 py-0.5 rounded-none`}>
      {icon}
      {status}
    </div>;
}